import { createContext, useContext } from "react"
import { isDeepEqual } from "remeda"
import type { StoreApi } from "zustand"
import { createStore, useStore } from "zustand"

import type { ActionDraft, DeviceSourceDraft, TriggerBuilderData } from "@/features/triggerBuilder/-lib/schema"
import { DEFAULT_GRACE_PERIOD_SECONDS, isSaveableTriggerData } from "@/features/triggerBuilder/-lib/schema"
import type { TriggerBuilderInit } from "@/features/triggerBuilder/utils/triggerToInit"
import { computed } from "@/lib/utils/zustand"

/*
 * ==========================================
 * Types & Constants
 * ==========================================
 */

export const TRIGGER_NEW_ID = "trigger::new"

const EMPTY_ACTION_DRAFT: ActionDraft = { title: "", body: "", to: [] }

const EMPTY_DEVICE_DRAFT: DeviceSourceDraft = {
  criteria: "INCLUDE",
  deviceIds: [],
  gracePeriodOnline: DEFAULT_GRACE_PERIOD_SECONDS,
  gracePeriodOffline: DEFAULT_GRACE_PERIOD_SECONDS
}

export type TriggerBuilderContextStore = {
  id: string
  thingId?: string
  /**
   * Set at creation from the role's permission (`update`, or `create` for a new draft). Fields render
   * read-only, the edit setters below are no-ops and `isDirty` is always false. Store-connected
   * components read it here; prop-driven children take it as a `readOnly` prop instead.
   */
  readOnly: boolean
  data: TriggerBuilderData
  derivedState: {
    isDirty: () => boolean
    /**
     * A trigger can be saved once it has a source (with its device chosen, for a single-device one),
     * and every present action has at least one recipient. See `isSaveableTriggerData` in `-lib/schema.ts`.
     */
    isComplete: () => boolean
  }
  actions: {
    setName: (name: string) => void
    setActive: (active: boolean) => void
    /**
     * Sets the Cloud Variable source. `thingId` updates the Thing context (used when picking a
     * variable across Things in the main Triggers section). Pass `undefined` to clear the source.
     * The Thing context then reverts to the builder's own scope.
     */
    setProperty: (propertyId: string | undefined, thingId?: string) => void
    /**
     * Sets the device-status source. Pass a partial to create (from empty) or patch the draft;
     * pass `null` to remove the source.
     */
    setDevice: (value: Partial<DeviceSourceDraft> | null) => void
    /** Creates (from empty), patches, or — with `null` — removes the e-mail action. */
    setEmail: (value: Partial<ActionDraft> | null) => void
    /** Creates (from empty), patches, or — with `null` — removes the push action. */
    setPush: (value: Partial<ActionDraft> | null) => void
    /**
     * Resets the editable data and the dirty baseline. With no argument it reverts to the current
     * baseline (last-saved values); pass `data` to adopt new values as the baseline — used to load
     * the full trigger detail into a list-seeded store, and to clear the bar after a save.
     */
    reset: (data?: TriggerBuilderData) => void
    /**
     * Adopts `name` and `active` changed outside the builder (menu rename or toggle) into the
     * baseline. A field is also written to the editable data unless it holds an unsaved local edit;
     * other unsaved edits are kept.
     */
    syncSaved: (fields: Pick<TriggerBuilderData, "name" | "active">) => void
  }
}

/*
 * ==========================================
 * Context & Hook
 * ==========================================
 */

export const TriggerBuilderContext = createContext<StoreApi<TriggerBuilderContextStore>>(
  null as unknown as StoreApi<TriggerBuilderContextStore>
)

export function useTriggerBuilderContext<T>(selector: (state: TriggerBuilderContextStore) => T) {
  const store = useContext(TriggerBuilderContext)

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!store) {
    throw new Error("Missing TriggerBuilderContext")
  }

  return useStore(store, selector)
}

/*
 * ==========================================
 * Store
 * ==========================================
 */

export function createTriggerBuilderContextStore(
  initialData: TriggerBuilderInit,
  currentUserId: string,
  { readOnly = false }: { readOnly?: boolean } = {}
): StoreApi<TriggerBuilderContextStore> {
  // Only a newly added action starts with the current user as a recipient; saved actions keep their
  // recipients as-is, so opening and saving a trigger never subscribes the viewer.
  const mergeAction = (prev: ActionDraft | undefined, value: Partial<ActionDraft>): ActionDraft =>
    prev ? { ...prev, ...value } : { ...EMPTY_ACTION_DRAFT, to: [currentUserId], ...value }

  const data: TriggerBuilderData = {
    name: initialData.name,
    active: initialData.active ?? false,
    source: initialData.source,
    email: initialData.email,
    push: initialData.push
  }

  // Editable baseline used for dirty comparison; updated on reset/hydrate.
  let baseline = data

  return createStore<TriggerBuilderContextStore>()((set, get) => {
    // User edits go through `edit`; `reset`/`syncSaved` use `set` directly, since external changes
    // (hydration, menu rename/toggle) must still land on a read-only draft.
    const edit = (update: (state: TriggerBuilderContextStore) => Partial<TriggerBuilderContextStore>) => {
      if (!readOnly) set(update)
    }

    return {
      id: initialData.id ?? TRIGGER_NEW_ID,
      thingId: initialData.thingId,
      readOnly,
      data: baseline,
      derivedState: {
        isDirty: computed(
          () => [get()],
          // Read-only drafts never count as dirty: no save bar, no unsaved-changes dialog, no badge.
          deps => !readOnly && !isDeepEqual(baseline, deps[0].data)
        ),
        isComplete: computed(
          () => [get()],
          deps => isSaveableTriggerData(deps[0].data)
        )
      },
      actions: {
        setName: name => edit(state => ({ data: { ...state.data, name } })),
        setActive: active => edit(state => ({ data: { ...state.data, active } })),
        setProperty: (propertyId, thingId) =>
          edit(state => ({
            data: { ...state.data, source: propertyId ? { kind: "property", propertyId } : undefined },
            // Clearing the variable drops the Thing it came from and restores the builder's own scope.
            // A Thing page keeps its Thing. The main Triggers section goes back to having none, so the
            // device source is not left locked to the former variable's Thing.
            thingId: propertyId ? (thingId ?? state.thingId) : initialData.thingId
          })),
        setDevice: value =>
          edit(state => ({
            data: {
              ...state.data,
              source:
                value === null
                  ? undefined
                  : {
                      kind: "device",
                      draft: {
                        ...(state.data.source?.kind === "device" ? state.data.source.draft : EMPTY_DEVICE_DRAFT),
                        ...value
                      }
                    }
            }
          })),
        setEmail: value =>
          edit(state => ({
            data: {
              ...state.data,
              email: value === null ? undefined : mergeAction(state.data.email, value)
            }
          })),
        setPush: value =>
          edit(state => ({
            data: {
              ...state.data,
              push: value === null ? undefined : mergeAction(state.data.push, value)
            }
          })),
        reset: next => {
          baseline = next ?? baseline
          set({ data: baseline })
        },
        syncSaved: ({ name, active }) => {
          const prev = baseline
          if (prev.name === name && prev.active === active) return
          baseline = { ...baseline, name, active }
          set(state => ({
            data: {
              ...state.data,
              name: state.data.name === prev.name ? name : state.data.name,
              active: state.data.active === prev.active ? active : state.data.active
            }
          }))
        }
      }
    }
  })
}
